import { useFlowControlStore } from '@components/flow-controller'
import { type InjectionKey, inject, provide } from 'vue'

import { type BundleStepType, triggerGA4CustomEvent } from '@/helpers/ga4'

/** Bundle identity and interaction counter for `bundle_step` events. */
export type BundleStepContext = {
  bundle_id: string
  bundle_name: string
  nextStepNumber: () => number
}

export const bundleStepContextKey: InjectionKey<BundleStepContext> = Symbol('bundleStepContext')

/**
 * Provide bundle identity and a page-scoped interaction counter. Call before
 * `FlowController` mounts — action components are torn down per step, so the
 * count can't live on them or be derived from `previousInputs.length`.
 */
export function provideBundleStepContext(flowDefinition: { id: string; name: string }): void {
  let interactionCount = 0

  provide(bundleStepContextKey, {
    bundle_id: flowDefinition.id,
    bundle_name: flowDefinition.name,
    nextStepNumber: () => (interactionCount += 1),
  })
}

const TRACKED_STEP_TYPES = new Set<string>([
  'pick_n',
  'pick_variants',
  'pick_date',
  'pick_time',
  'survey',
] satisfies BundleStepType[])

/** Per-interaction `bundle_step` events — counterpart to `useFlowGa4Tracking`. */
export function useBundleStepTracking() {
  const store = useFlowControlStore()
  const context = inject(bundleStepContextKey, null)

  /** @param value Resulting state after the interaction — see `flowStepValue.ts`. */
  function trackBundleStep(value: string): void {
    if (!context) return

    const stepType = store.action?.type
    if (!stepType || !TRACKED_STEP_TYPES.has(stepType)) return

    triggerGA4CustomEvent('bundle_step', {
      bundle_id: context.bundle_id,
      bundle_name: context.bundle_name,
      bundle_step_number: context.nextStepNumber(),
      bundle_step_type: stepType as BundleStepType,
      bundle_step_value: value,
    })
  }

  return { trackBundleStep }
}
